import { Platform } from 'react-native';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { resolveDeviceCommandInboxActive } from '../../shell/feature-flags/deviceCommandInbox';
import { supabase } from '../../shared/supabase/supabaseClient';
import { getOrCreateSyncDeviceId } from './syncDeviceId';
import { buildCatalogSyncOperationId } from './catalogSyncOperationId';
import { resolveCustomerSyncOperationId } from './customerSyncOperationId';
import type { SyncEntity, SyncOperation } from './syncTypes';

const INBOX_HOT_SET: ReadonlySet<SyncEntity> = new Set(['cash_session']);

export function isInboxHotSetEntity(entity: SyncEntity): boolean {
  return INBOX_HOT_SET.has(entity);
}

export type IngestCommandInput = {
  entityKind: SyncEntity;
  operation: SyncOperation;
  operationId: string;
  payload: Record<string, unknown>;
};

export type IngestCommandOutcome =
  | 'applied'
  | 'idempotent'
  | 'queued'
  | 'failed'
  | 'rejected';

export type IngestCommandResult = {
  operationId: string;
  entityKind: SyncEntity;
  outcome: IngestCommandOutcome;
  status?: string;
  error?: string | null;
  idempotent?: boolean;
};

export type IngestBatchResponse = {
  ok: boolean;
  error?: string;
  results?: IngestCommandResult[];
};

function ingestPlatform(): 'web' | 'ios' | 'android' {
  if (Platform.OS === 'ios' || Platform.OS === 'android') return Platform.OS;
  return 'web';
}

function toIngestRpcCommand(cmd: IngestCommandInput) {
  return {
    entityKind: cmd.entityKind,
    operation: cmd.operation,
    operationId: cmd.operationId,
    payload: cmd.payload,
  };
}

function pickPayloadId(payload: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

type OperationIdSpec = {
  idKeys: string[];
  suffix?: string;
  catalogLww?: boolean;
};

/** Claves de operationId por entidad. El stamp web solo funciona si esto devuelve un id. */
const OPERATION_ID_SPECS: Partial<Record<SyncEntity, OperationIdSpec>> = {
  sale: { idKeys: ['saleId'] },
  sale_void: { idKeys: ['saleId'], suffix: ':void' },
  cash_session: { idKeys: ['sessionId'] },
  cash_outflow: { idKeys: ['outflowId'] },
  cash_outflow_void: { idKeys: ['outflowId'], suffix: ':void' },
  stock_adjustment: { idKeys: ['movementId'] },
  stock_transfer: { idKeys: ['transferId'] },
  stock_lot: { idKeys: ['lotId'] },
  stock_lot_allocation: { idKeys: ['allocationId'] },
  expense: { idKeys: ['expenseId'] },
  expense_void: { idKeys: ['expenseId'], suffix: ':void' },
  expense_settlement: { idKeys: ['settlementId'] },
  expense_settlement_void: { idKeys: ['settlementId'], suffix: ':void' },
  expense_attachment: { idKeys: ['attachmentId'] },
  purchase_order: { idKeys: ['purchaseOrderId'] },
  purchase_order_void: { idKeys: ['purchaseOrderId'], suffix: ':void' },
  purchase_order_close: { idKeys: ['purchaseOrderId'], suffix: ':close' },
  purchase_reception: { idKeys: ['receptionId'] },
  purchase_reception_with_expense: { idKeys: ['receptionId'], suffix: ':with-expense' },
  purchase_reception_void: { idKeys: ['receptionId'], suffix: ':void' },
  purchase_return: { idKeys: ['returnId'] },
  purchase_return_void: { idKeys: ['returnId'], suffix: ':void' },
  branch_transfer: { idKeys: ['transferId'] },
  branch_transfer_void: { idKeys: ['transferId'], suffix: ':void' },
  product: { idKeys: ['id', 'productId'], catalogLww: true },
  product_variant: { idKeys: ['id'], catalogLww: true },
  product_catalog_event: { idKeys: ['id'] },
  brand: { idKeys: ['id', 'brandId'], catalogLww: true },
  supplier: { idKeys: ['id', 'supplierId'], catalogLww: true },
  category: { idKeys: ['id', 'categoryId'], catalogLww: true },
  promotion: { idKeys: ['id'], catalogLww: true },
  promotion_event: { idKeys: ['id'] },
  branch_settings: { idKeys: ['id', 'branchId'], catalogLww: true },
  store_profile: { idKeys: ['id'] },
  scheduled_closure: { idKeys: ['id'] },
  kitchen_bump: { idKeys: ['id'] },
  external_service: { idKeys: ['id'] },
  physical_count_batch: { idKeys: ['batchId'] },
};

export function extractOperationId(
  entity: SyncEntity,
  _operation: SyncOperation,
  payload: Record<string, unknown>,
): string | null {
  const raw = payload.operationId;
  if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();

  if (entity === 'fiado') {
    return resolveCustomerSyncOperationId(payload) ?? null;
  }

  const spec = OPERATION_ID_SPECS[entity];
  if (!spec) return null;
  const id = pickPayloadId(payload, ...spec.idKeys);
  if (!id) return null;
  if (spec.catalogLww) return buildCatalogSyncOperationId(id, payload);
  return spec.suffix ? `${id}${spec.suffix}` : id;
}

/**
 * Errores de red transitorios (iOS Safari "Load failed", Chrome "Failed to fetch",
 * timeouts, aborts). El fetch subyacente de supabase-js los lanza como TypeError
 * sin pasar por el canal `error` del RPC — escapan sin estructura y llegan a
 * Sentry como bugs. Reintentar con backoff los absorbe en la mayoría de los casos.
 */
function isTransientNetworkError(text: string): boolean {
  const msg = text.toLowerCase();
  return (
    msg.includes('load failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('a network error occurred') ||
    msg.includes('err_network') ||
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('connection refused') ||
    msg.includes('connection reset') ||
    msg.includes('aborted')
  );
}

const INGEST_MAX_RETRIES = 2;
const INGEST_BACKOFF_MS = 400;

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function ingestDeviceCommands(
  commands: IngestCommandInput[],
  branchId?: string | null,
): Promise<IngestBatchResponse> {
  if (commands.length === 0) {
    return { ok: true, results: [] };
  }
  if (commands.length > 50) {
    return { ok: false, error: 'batch_too_large' };
  }

  const deviceId = await getOrCreateSyncDeviceId();
  const rpcArgs = {
    p_commands: commands.map(toIngestRpcCommand),
    p_device_id: deviceId,
    p_platform: ingestPlatform(),
    p_branch_id: branchId ?? null,
  };

  let data: unknown = null;
  let rpcError: { message: string } | null = null;

  // Retry con backoff para errores de red transitorios. El fetch de supabase-js
  // en iOS Safari falla con TypeError "Load failed" ante caídas de red breves
  // (mobile, handoff entre wifi/cellular). Sin retry el error escapa como bug
  // y el usuario reintenta manualmente (Sentry VUELTO-NEGOCIO-23: 17 eventos
  // de 1 usuario). Con 2 reintentos + 400ms la mayoría se absorbe.
  for (let attempt = 0; attempt <= INGEST_MAX_RETRIES; attempt++) {
    try {
      const result = await supabase.rpc('ingest_device_commands', rpcArgs);
      data = result.data;
      rpcError = result.error;
      // Errores de red devueltos por el canal `error` del RPC también son
      // candidatos a retry (supabase-js a veces los envuelve acá).
      if (rpcError && isTransientNetworkError(rpcError.message) && attempt < INGEST_MAX_RETRIES) {
        await sleep(INGEST_BACKOFF_MS * (attempt + 1));
        continue;
      }
      break;
    } catch (thrown) {
      const text = errorText(thrown);
      if (isTransientNetworkError(text) && attempt < INGEST_MAX_RETRIES) {
        await sleep(INGEST_BACKOFF_MS * (attempt + 1));
        continue;
      }
      // No transitorio o reintentos agotados: devolver error estructurado para
      // que fluya por captureOpsFailure (que clasifica transitorios como
      // esperados y no los reporta como bug).
      return { ok: false, error: text || 'ingest_failed' };
    }
  }

  if (rpcError) {
    return { ok: false, error: rpcError.message };
  }

  const body = data as {
    ok?: boolean;
    error?: string;
    results?: Array<{
      operationId?: string;
      entityKind?: SyncEntity;
      outcome?: string;
      status?: string;
      error?: string | null;
      idempotent?: boolean;
    }>;
  };

  if (!body?.ok) {
    return { ok: false, error: body?.error ?? 'ingest_failed' };
  }

  const results: IngestCommandResult[] = (body.results ?? []).map((row, index) => {
    const cmd = commands[index];
    return {
      operationId: row.operationId ?? cmd?.operationId ?? '',
      entityKind: (row.entityKind ?? cmd?.entityKind ?? 'sale') as SyncEntity,
      outcome: (row.outcome ?? 'failed') as IngestCommandOutcome,
      status: row.status,
      error: row.error,
      idempotent: row.idempotent,
    };
  });

  return { ok: true, results };
}

export function isSaleInboxEntity(entity: SyncEntity): boolean {
  return entity === 'sale' || entity === 'sale_void';
}

export async function shouldUseInboxIngest(entity: SyncEntity): Promise<boolean> {
  if (!(await isOnline().catch(() => false))) return false;
  if (!(await resolveDeviceCommandInboxActive())) return false;
  // sale/sale_void salieron del hot-set (S2): van por process_sale_command / void remoto.
  return isInboxHotSetEntity(entity);
}

export async function ingestSingleCommand(
  entity: SyncEntity,
  operation: SyncOperation,
  payload: Record<string, unknown>,
  branchId?: string | null,
): Promise<IngestCommandResult> {
  const operationId = extractOperationId(entity, operation, payload);
  if (!operationId) {
    return {
      operationId: '',
      entityKind: entity,
      outcome: 'rejected',
      error: 'operationId_required',
    };
  }

  const withOp =
    payload.operationId === operationId ? payload : { ...payload, operationId };

  const response = await ingestDeviceCommands(
    [{ entityKind: entity, operation, operationId, payload: withOp }],
    branchId,
  );

  if (!response.ok) {
    if (response.error === 'inbox_disabled') {
      return {
        operationId,
        entityKind: entity,
        outcome: 'failed',
        error: 'inbox_disabled',
      };
    }
    return {
      operationId,
      entityKind: entity,
      outcome: 'failed',
      error: response.error ?? 'ingest_failed',
    };
  }

  return (
    response.results?.[0] ?? {
      operationId,
      entityKind: entity,
      outcome: 'failed',
      error: 'empty_ingest_result',
    }
  );
}

export function ingestOutcomeSucceeded(outcome: IngestCommandOutcome): boolean {
  return outcome === 'applied' || outcome === 'idempotent';
}
